<script setup lang="ts">
import { useTemplateRef } from "vue";
import type { FloatingItemData } from "./menu.types";
import { floatingSubmenuItemActive } from "./menu.styles";
import type { menuClasses } from "./menu.styles";
import { useMenuFloatingPanel } from "./useMenuFloatingPanel";

/**
 * Subset of `menuClasses` output consumed by this panel. Narrowed so the
 * parent's full class bag doesn't leak into the child's typing.
 */
export type MenuFloatingPanelClasses = Pick<
  ReturnType<typeof menuClasses>,
  | "floatingTooltip"
  | "floatingHeader"
  | "floatingSubmenu"
  | "floatingSubmenuItem"
  | "floatingSubmenuIcon"
  | "floatingSubmenuContent"
>;

export type { MenuNavigateDetail } from "./menu.types";

interface Props {
  data: FloatingItemData | null;
  classes: MenuFloatingPanelClasses;
  isPathActive: (to: string | undefined) => boolean;
}

defineProps<Props>();

const rootRef = useTemplateRef<HTMLElement>("rootRef");
const { panelCardCls, emitNavigate } = useMenuFloatingPanel({ rootRef });
</script>

<template>
  <e-primitive ref="rootRef" :inner-class="panelCardCls">
    <span v-if="!data?.children?.length" :class="classes.floatingTooltip">
      {{ data?.label }}
    </span>
    <template v-else>
      <span :class="classes.floatingHeader">{{ data.label }}</span>
      <div :class="classes.floatingSubmenu">
        <a
          v-for="(child, i) in data.children"
          :key="child.to ?? `${child.label}-${i}`"
          :href="child.to"
          :target="child.target ?? undefined"
          :rel="child.target === '_blank' ? 'noopener noreferrer' : undefined"
          :class="[classes.floatingSubmenuItem, isPathActive(child.to) && floatingSubmenuItemActive]"
          :aria-current="isPathActive(child.to) ? 'page' : undefined"
          @click="emitNavigate($event, child.to, child.target)"
        >
          <e-icon v-if="child.icon" :name="child.icon" size="sm" :class="classes.floatingSubmenuIcon"></e-icon>
          <span :class="classes.floatingSubmenuContent">{{ child.label }}</span>
        </a>
      </div>
    </template>
  </e-primitive>
</template>
